import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Link2, Loader2, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";

/**
 * Owner-only panel that lists active shareable invite links and lets the
 * owner mint new ones. Anyone with the link can join the workspace until
 * the link expires / hits its max-uses cap / gets revoked.
 *
 * Backend gates: only company (non-personal) workspaces, owner role.
 * This component additionally hides itself when those conditions aren't
 * met, but the backend remains the source of truth.
 */
export function InviteLinksSection({ workspace }) {
  const [links, setLinks] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [showLink, setShowLink] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    if (!workspace?.id) return;
    try {
      const { data } = await api.get(`/workspaces/${workspace.id}/invite-links`);
      setLinks(data);
    } catch (e) {
      // 403 (non-owner) silently hides — backend is the source of truth.
      setLinks([]);
    }
  }, [workspace?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevoke = async (link) => {
    const ok = await confirm({
      title: "Revoke this invite link?",
      description: "The link will stop working immediately. People who already joined keep their access.",
      confirmLabel: "Revoke link",
      danger: true,
      testId: "confirm-revoke-link",
    });
    if (!ok) return;
    try {
      await api.delete(`/workspaces/${workspace.id}/invite-links/${link.id}`);
      toast.success("Invite link revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!links) {
    return (
      <div className="surface-card p-5 text-sm text-[#94A3B8] dark:text-white/40">
        Loading invite links…
      </div>
    );
  }

  return (
    <div className="surface-card p-0 overflow-hidden mb-6" data-testid="invite-links-section">
      <div className="px-5 py-4 border-b border-[#E5E7EB] dark:border-white/10 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-['Geist'] font-semibold text-base text-[#0F172A] dark:text-white">
            Shareable invite links
          </h2>
          <p className="text-xs text-[#64748B] dark:text-white/60 mt-0.5">
            Send one link to onboard many teammates. Restrict by domain, set an expiry, cap the seats.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="create-invite-link-btn"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 text-sm font-medium rounded-md bg-[#0F172A] text-white hover:bg-[#1E293B] dark:bg-white dark:text-[#0F172A] dark:hover:bg-white/90"
        >
          <Plus size={13} /> New link
        </button>
      </div>

      {links.length === 0 ? (
        <div className="px-5 py-6 text-sm text-[#64748B] dark:text-white/60 flex items-center gap-2">
          <Link2 size={14} className="text-[#94A3B8]" />
          No active invite links yet.
        </div>
      ) : (
        <ul className="divide-y divide-[#E5E7EB] dark:divide-white/10">
          {links.map((link) => (
            <InviteLinkRow
              key={link.id}
              link={link}
              onCopy={() => {
                navigator.clipboard
                  ?.writeText(link.url)
                  .then(() => toast.success("Invite link copied"))
                  .catch(() => toast.error("Copy failed — please copy manually"));
              }}
              onRevoke={() => handleRevoke(link)}
            />
          ))}
        </ul>
      )}

      <CreateInviteLinkDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        workspaceId={workspace?.id}
        onCreated={(created) => {
          setShowLink(created);
          load();
        }}
      />

      <CreatedLinkDialog open={!!showLink} onOpenChange={(o) => !o && setShowLink(null)} link={showLink} />
    </div>
  );
}

function InviteLinkRow({ link, onCopy, onRevoke }) {
  const domainBadge = link.allowed_domains.length
    ? link.allowed_domains.map((d) => `@${d}`).join(", ")
    : "Any domain";
  const expiry = useMemo(() => {
    if (!link.expires_at) return "No expiry";
    const date = new Date(link.expires_at);
    if (Number.isNaN(date.getTime())) return "—";
    const diffMs = date.getTime() - Date.now();
    const days = Math.round(diffMs / 86400000);
    if (days <= 0) return "Expires today";
    if (days === 1) return "Expires tomorrow";
    return `Expires in ${days} days`;
  }, [link.expires_at]);
  const usage = link.max_uses
    ? `${link.use_count} / ${link.max_uses} used`
    : `${link.use_count} joined`;

  return (
    <li
      className="px-5 py-4 flex items-center justify-between gap-4"
      data-testid={`invite-link-row-${link.id}`}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#3B82F6]/10 text-[#1D4ED8] text-[11px] font-semibold uppercase tracking-wide capitalize dark:bg-[#3B82F6]/15 dark:text-[#93C5FD]">
            <ShieldCheck size={10} /> {link.role}
          </span>
          <span className="px-1.5 py-0.5 rounded-md bg-[#F1F5F9] text-[#475569] text-[11px] font-medium dark:bg-white/[0.06] dark:text-white/70">
            {domainBadge}
          </span>
          <span className="text-[11px] text-[#94A3B8] dark:text-white/40">{expiry} · {usage}</span>
        </div>
        <p className="font-mono text-xs break-all text-[#475569] dark:text-white/60">{link.url}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={onCopy}
          title="Copy link"
          data-testid={`invite-link-copy-${link.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] dark:bg-transparent dark:border-white/10 dark:text-white/70 dark:hover:bg-white/[0.06]"
        >
          <Copy size={13} />
        </button>
        <button
          type="button"
          onClick={onRevoke}
          title="Revoke"
          data-testid={`invite-link-revoke-${link.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white text-[#B91C1C] hover:bg-[#FEF2F2] dark:bg-transparent dark:border-white/10 dark:text-[#FCA5A5] dark:hover:bg-[#7F1D1D]/30"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </li>
  );
}

function CreateInviteLinkDialog({ open, onOpenChange, workspaceId, onCreated }) {
  const [role, setRole] = useState("editor");
  const [domainsInput, setDomainsInput] = useState("");
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [maxUsesInput, setMaxUsesInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRole("editor");
    setDomainsInput("");
    setExpiresInDays(7);
    setMaxUsesInput("");
    setBusy(false);
  }, [open]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      const allowed_domains = domainsInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const max_uses = maxUsesInput ? Number(maxUsesInput) : null;
      const body = {
        role,
        allowed_domains: allowed_domains.length ? allowed_domains : null,
        expires_in_days: Number(expiresInDays),
        max_uses: max_uses && max_uses > 0 ? max_uses : null,
      };
      const { data } = await api.post(`/workspaces/${workspaceId}/invite-links`, body);
      navigator.clipboard?.writeText(data.url).catch(() => {});
      toast.success("Invite link created and copied");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] rounded-xl border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10"
        data-testid="create-invite-link-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">
            Create invite link
          </DialogTitle>
          <DialogDescription>
            Share one URL with multiple teammates. Each click counts toward the cap until it expires.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569] dark:text-white/60">Role for joiners</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger
                data-testid="invite-link-role"
                className="h-10 rounded-md border-[#E5E7EB] text-sm focus:ring-[#3B82F6] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-md border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10">
                <SelectItem value="editor" className="text-sm">Editor — can create, edit, and run tests</SelectItem>
                <SelectItem value="viewer" className="text-sm">Viewer — read-only access</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#475569] dark:text-white/60">
              Allowed email domains <span className="text-[#94A3B8] font-normal">(optional)</span>
            </label>
            <Input
              value={domainsInput}
              onChange={(e) => setDomainsInput(e.target.value)}
              placeholder="fastcollab.com, partner.com"
              data-testid="invite-link-domains"
              className="rounded-md border-[#E5E7EB] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
            />
            <p className="text-[11px] text-[#94A3B8] dark:text-white/40">
              Comma-separated. Leave blank to allow any email address.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569] dark:text-white/60">Expires in (days)</label>
              <Input
                type="number"
                min={1}
                max={90}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                data-testid="invite-link-expires"
                className="rounded-md border-[#E5E7EB] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-[#475569] dark:text-white/60">
                Max uses <span className="text-[#94A3B8] font-normal">(blank = ∞)</span>
              </label>
              <Input
                type="number"
                min={1}
                max={10000}
                value={maxUsesInput}
                onChange={(e) => setMaxUsesInput(e.target.value)}
                placeholder="e.g. 25"
                data-testid="invite-link-max-uses"
                className="rounded-md border-[#E5E7EB] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <button
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="invite-link-cancel"
            className="h-9 px-4 rounded-md text-sm font-medium border border-[#E5E7EB] text-[#0F172A] hover:bg-[#F8FAFC] dark:bg-transparent dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={busy || !expiresInDays || Number(expiresInDays) < 1 || Number(expiresInDays) > 90}
            data-testid="invite-link-create"
            className="h-9 px-4 rounded-md text-sm font-medium bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            Create link
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatedLinkDialog({ open, onOpenChange, link }) {
  if (!link) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[520px] rounded-xl border-[#E5E7EB] dark:bg-[#161618] dark:border-white/10"
        data-testid="invite-link-created-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Geist'] font-semibold tracking-tight">Invite link ready</DialogTitle>
          <DialogDescription>
            Already copied to your clipboard. Send it to your teammates over any channel.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-[#3B82F6]/20 bg-[#3B82F6]/5 p-3 flex items-center gap-2">
          <p className="font-mono text-xs text-[#0F172A] dark:text-white break-all flex-1">{link.url}</p>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText(link.url).then(() => toast.success("Copied"));
            }}
            data-testid="invite-link-created-copy"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white text-[#475569] hover:bg-[#F8FAFC] shrink-0 dark:bg-transparent dark:border-white/10 dark:text-white/70"
          >
            <Copy size={13} />
          </button>
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="invite-link-created-close"
            className="h-9 px-4 rounded-md text-sm font-medium bg-[#0F172A] text-white hover:bg-[#1E293B] dark:bg-white dark:text-[#0F172A] dark:hover:bg-white/90"
          >
            Done
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
